import { ClientApplication } from '@azure/msal-browser/dist/app/ClientApplication';
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { apiConfig } from "../../authConfig";
import { ModalNotification } from "../ModalNotification";
import { ModalConfirm } from "../ModalConfirm";

import { GlobalContext } from "../../utils/GlobalContext";
import React, { useEffect, useRef, useState, useContext } from 'react';
import { ReactCropperElement, Cropper } from 'react-cropper';
import "cropperjs/dist/cropper.css";
import { Client, PayorInfoType, VwAuthorizationDetail, AuthDenialReasonLookup, AuthorizationStatus, ChoiceOption, VwAuthorizationHistory, VwAuthorization, AuthActionLookup, AuthorizationAction, EzClaimAuthObject, AuthFeedbackLookup, VwAuthorizationFeedback, VwAuthorizationDenailReasons, APIReturnObject, Status } from '../../utils/Definitions';
import { DatePicker } from "@mui/x-date-pickers";
import { TextField } from '@mui/material';
import Select from "react-select";
import { ValueContainer } from "../../components/ValueContainer";
import { addDays, format, isAfter, isBefore, isSameDay, isYesterday, parse, parseISO, set, subDays } from "date-fns";

import ValueType from "react-select";
import OptionType from "react-select";
import { SingleValue, MultiValue, OnChangeValue } from "react-select";
import { ProfilePicture } from '../ProfilePicture';

interface Props {
    Client: Client | null,
    AuthId: string | null,
    AuthPayorType: string | null,
    PayorInfoTypes: PayorInfoType[],
    AuthorizationStatuses: AuthorizationStatus[],
    AuthorizationDenialReasons: AuthDenialReasonLookup[],
    AuthorizationFeedbackOptions: AuthFeedbackLookup[],
    AuthorizationActions: AuthActionLookup[],
    Active: boolean,
    setActive: React.Dispatch<React.SetStateAction<boolean>>;
    OnModalClose: any;
    IsInsurance: boolean;
}

const buckets = [
    { value: '0', label: 'Non-Bucket' },
    { value: '1', label: 'Bucket' },
]

const yesNoValues = [
    { value: '1', label: "Yes" },
    { value: '0', label: "No" },
]

export const AuthorizationModal: React.FC<Props> = (props) => {

    const globalContext = useContext(GlobalContext);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [saveButtonActive, setSaveButtonActive] = useState<boolean>(false);
    const [saveButtonActiveForUpdate, setSaveButtonActiveForUpdate] = useState<boolean>(false);
    const [showDenialReasonsDropdown, setShowDenialReasonsDropdown] = useState<boolean>(false);
    const [showP2PInputs, setShowP2PInputs] = useState<boolean>(false);

    const [authorization, setAuthorization] = useState<VwAuthorization | null>(null);
    const [authorizationDetails, setAuthorizationDetails] = useState<VwAuthorizationDetail[]>([]);
    const [authorizationDetailsFiltered, setAuthorizationDetailsFiltered] = useState<VwAuthorizationDetail[]>([]);
    const [authorizationHistory, setAuthorizationHistory] = useState<VwAuthorizationHistory[]>([]);
    const [filteredAuthActions, setFilteredAuthActions] = useState<AuthActionLookup[]>([]);
    const [prevAuthAction, setPrevAuthAction] = useState<AuthorizationAction | null>(null);

    const [authComment, setAuthComment] = useState<string>("");
    const [reportDueDate, setReportDueDate] = useState<Date | null>(null);
    const [isBucket, setIsBucket] = useState<ChoiceOption | null>(null);
    const [selectedPayorType, setSelectedPayorType] = useState<ChoiceOption | null>(null);
    const [selectedAuthStatus, setSelectedAuthStatus] = useState<ChoiceOption | null>(null);
    const [prevSelectedAuthStatus, setPrevSeelectedAuthStatus] = useState<ChoiceOption | null>(null);
    const [reportCompleted, setReportCompleted] = useState<ChoiceOption | null>(null);
    const [actionDueDate, setActionDueDate] = useState<Date | null>(null);
    const [selectedAuthAction, setSelectedAuthAction] = useState<ChoiceOption | null>(null)
    const [actionComplete, setActionComplete] = useState<ChoiceOption | null>(null);
    const [selectedAuthDenialReason, setSelectedAuthDenialReason] = useState<ChoiceOption[] | null>(null);

    // P2P values
    const [selectedAuthFeedbackOptions, setSelectedAuthFeedbackOptions] = useState<ChoiceOption[] | null>(null);
    const [payorRepOnCallValue, setPayorRepOnCallValue] = useState<string | null>(null);

    // State to track the checked status of checkboxes based on authId
    const [checkedAuthNumber, setCheckedAuthNumber] = useState<string | null>(null);
    const [ezClaimAuthorizations, setEzClaimAuthorizations] = useState<EzClaimAuthObject[]>([]);
    const [clientAuthorizations, setClientAuthorizations] = useState<VwAuthorizationDetail[]>([]); // Master list with everything

    const [isSaving, setIsSaving] = useState<boolean>(false);

    const [warningModalActive, setWarningModalActive] = useState<boolean>(false);
    const [deleteAuthorizationModal, setDeleteAuthorizationModal] = useState<boolean>(false);

    interface ProcedureValue {
        cptCode: string;
        unitsRequested: string;
    }

    interface ProcedureValues {
        [authId: string]: {
            [procedureCode: string]: ProcedureValue;
        };
    }

    const [procedureValues, setProcedureValues] = useState<{ id: string; unitsRequested: string }[]>([]);


    // For update
    const [isUpdateView, setIsUpdateView] = useState<boolean>(false);


    useEffect(() => {
        if (props.Active) {
            GetAuthorization();
            GetAuthorizationHistory();
            GetAuthorizationDetails();
            GetAuthorizationsFromEzClaim();
            GetAuthorizationsForClient();
            // Auth action and date will not load here because of a timing issue.
            //GetAuthAction();
        }
    }, [props.Active]);

    useEffect(() => {
        if (authorization) {
            SetSelectedPayorTypeValueOnLoad();
            SetSelectedAuthStatusValueOnLoad();
            SetSelectedBucketValueOnLoad();
            SetSelectedReportCompletedOnLoad();
            SetSelectedReportDueDateOnLoad();
            GetAuthAction();
        }
    }, [authorization]);

    useEffect(() => {
        if (authorization && prevAuthAction) {
            SetSelectedAuthActionOnLoad();
            SetActionCompletedOnLoad();
            SetActionDueDateValueOnLoad();
        }
    }, [prevAuthAction, filteredAuthActions]);

    useEffect(() => {
        if (isUpdateView) {
            GetAuthorizationsFromEzClaim();
        }
    }, [isUpdateView]);

    useEffect(() => {
        if (authorizationDetails) {
            var temp = authorizationDetails.filter(x => x.authActive === true && x.detailActive === true);

            setAuthorizationDetailsFiltered(temp);
        }
    }, [authorizationDetails]);

    useEffect(() => {
        if (selectedAuthStatus && props.AuthorizationActions) {
            var foundAuthActions = props.AuthorizationActions.filter(x => String(x.authStatusId).toLowerCase() === String(selectedAuthStatus.value).toLowerCase());

            setFilteredAuthActions(foundAuthActions);
            if (foundAuthActions.length === 1) {
                var temp = ({ value: String(foundAuthActions[0].authActionId), label: String(foundAuthActions[0].actionText) });
                setSelectedAuthAction(temp);
            }
            else {
                setSelectedAuthAction(null);
            }

            if (selectedAuthStatus.value === '3' || selectedAuthStatus.value === '4') {
                SetDenialReasonsOnLoad();
                setShowDenialReasonsDropdown(true);
            }
            else {
                setShowDenialReasonsDropdown(false);
                setSelectedAuthDenialReason(null);
            }
        }
    }, [selectedAuthStatus]);

    useEffect(() => {
        if (selectedAuthAction) {

            if (selectedAuthAction.value === '2' || selectedAuthAction.value === '8' || selectedAuthAction.value === '16') {
                SetPayorRepOnCallOnLoad();
                SetFeedbackValuesOnLoad();
                setShowP2PInputs(true);
            }
            else {
                setShowP2PInputs(false);

                setSelectedAuthFeedbackOptions(null);
                setPayorRepOnCallValue(null);
            }

        }
    }, [selectedAuthAction]);

    function SetSelectedReportDueDateOnLoad() {
        if (authorization) {
            setReportDueDate(authorization.reportDueDate);
        }
    }

    function GetAuthAction() {
        if (props.AuthId && authorization) {

            setIsLoading(true);

            globalContext.GetToken().then((token: any) => {
                var headers = new Headers();
                var bearer = "Bearer " + token;
                headers.append("Authorization", bearer);
                headers.append("Content-type", "application/json;charset=UTF-8");

                var options = {
                    method: "GET",
                    headers: headers,
                };

                var url = apiConfig.apiEndpoint + "/Credentials/GetAuthorizationActionByAuthId?authId=" + props.AuthId;
                fetch(url, options)
                    .then(res => res.json())
                    .then(
                        (result) => {
                            setPrevAuthAction(result);
                            setIsLoading(false);
                        },
                        (error) => {
                            setIsLoading(false);
                            globalContext.setErrorModalProps({ errorMessage: error, errorLocation: "/Credentials/GetAuthorizationActionByAuthId" });
                            globalContext.setErrorModalActive(true);
                        }
                    )
            })

        }
    }

    function SetSelectedAuthActionOnLoad() {
        if (prevAuthAction) {
            var foundActionType = props.AuthorizationActions.find(x => String(x.authActionId).toLowerCase() === String(prevAuthAction.authActionId).toLowerCase());
            var temp: ChoiceOption;

            if (foundActionType) {
                temp = ({ value: String(foundActionType.authActionId), label: foundActionType.actionText })
                setSelectedAuthAction(temp);
            }
            else {
                setShowP2PInputs(false);
            }
        }
    }

    function SetSelectedReportCompletedOnLoad() {
        if (authorization) {
            const targetValue = authorization.reportCompleted ? "1" : "0";

            var foundValue = yesNoValues?.find(z => z.value === targetValue);
            var temp: ChoiceOption;

            if (foundValue) {
                temp = ({ value: String(foundValue.value), label: foundValue.label })
                setReportCompleted(temp);
            }
        }
    }

    function SetSelectedBucketValueOnLoad() {
        if (authorization && authorization.bucket !== null) {

            const targetValue = authorization.bucket ? "1" : "0";

            var foundBucket = buckets?.find(z => z.value === targetValue);
            var temp: ChoiceOption;

            if (foundBucket) {
                temp = ({ value: String(foundBucket.value), label: foundBucket.label })
                setIsBucket(temp);
            }

        }
    }

    function SetSelectedPayorTypeValueOnLoad() {
        if (props.PayorInfoTypes && authorization) {

            var foundPayorType = props.PayorInfoTypes?.find(z => String(z.payorInfoTypeId).toLowerCase() === String(authorization.payorType).toLowerCase());
            var temp: ChoiceOption;

            if (foundPayorType) {
                temp = ({ value: String(foundPayorType.payorInfoTypeId), label: foundPayorType.payorInfoType1 })
                setSelectedPayorType(temp);
            }

        }
    }

    function SetSelectedAuthStatusValueOnLoad() {
        if (props.AuthorizationStatuses && authorization) {

            var foundAuthStatus = props.AuthorizationStatuses?.find(z => String(z.authStatusId) === String(authorization.statusId));
            var temp: ChoiceOption;

            if (foundAuthStatus) {
                temp = ({ value: String(foundAuthStatus.authStatusId), label: foundAuthStatus.status })
                setSelectedAuthStatus(temp);
                setPrevSeelectedAuthStatus(temp);
            }

        }
    }

    function SetActionDueDateValueOnLoad() {
        if (prevAuthAction) {
            if (prevAuthAction.dueDate) {
                setActionDueDate(prevAuthAction.dueDate);
            }
        }
    }

    function SetPayorRepOnCallOnLoad() {
        if (authorization && authorization.payorRepOnCall) {
            setPayorRepOnCallValue(authorization.payorRepOnCall);
        }
    }

    function SetActionCompletedOnLoad() {
        if (authorization && prevAuthAction) {
            const targetValue = prevAuthAction.actionCompleted ? "1" : "0";

            var foundValue = yesNoValues?.find(z => z.value === targetValue);
            var temp: ChoiceOption;

            if (foundValue) {
                temp = ({ value: String(foundValue.value), label: foundValue.label })
                setActionComplete(temp);
            }
        }
    }

    // multi values for feedback
    function SetFeedbackValuesOnLoad() {
        GetFeedbackForAuth();
    }

    function SetDenialReasonsOnLoad() {
        GetDenialReasons();
    }


    function GetFeedbackForAuth() {
        if (props.AuthId) {
            globalContext.GetToken().then((token: any) => {
                var headers = new Headers();
                var bearer = "Bearer " + token;
                headers.append("Authorization", bearer);
                headers.append("Content-type", "application/json;charset=UTF-8");

                var options = {
                    method: "GET",
                    headers: headers,
                };

                var url = apiConfig.apiEndpoint + "/Credentials/GetFeedbackByAuthId?authId=" + props.AuthId;
                fetch(url, options)
                    .then(res => res.json())
                    .then(
                        (result) => {
                            const mappedOptions = result.map((item: VwAuthorizationFeedback) => ({
                                value: item.authFeedbackId.toString(),  // or item.id or whatever makes sense based on your API response
                                label: item.feedbackText,  // same here
                            }));
                            setSelectedAuthFeedbackOptions(mappedOptions);

                            setIsLoading(false);
                        },
                        (error) => {
                            setIsLoading(false);
                            globalContext.setErrorModalProps({ errorMessage: error, errorLocation: "/Credentials/GetFeedbackByAuthId" });
                            globalContext.setErrorModalActive(true);
                        }
                    )
            })
        }
    }

    function GetDenialReasons() {
        if (props.AuthId) {
            globalContext.GetToken().then((token: any) => {
                var headers = new Headers();
                var bearer = "Bearer " + token;
                headers.append("Authorization", bearer);
                headers.append("Content-type", "application/json;charset=UTF-8");

                var options = {
                    method: "GET",
                    headers: headers,
                };

                var url = apiConfig.apiEndpoint + "/Credentials/GetDenialReasonsByAuthId?authId=" + props.AuthId;
                fetch(url, options)
                    .then(res => res.json())
                    .then(
                        (result) => {
                            const mappedOptions = result.map((item: VwAuthorizationDenailReasons) => ({
                                value: item.denialReasonId.toString(),  // or item.id or whatever makes sense based on your API response
                                label: item.reasonText,  // same here
                            }));
                            setSelectedAuthDenialReason(mappedOptions);

                            setIsLoading(false);
                        },
                        (error) => {
                            setIsLoading(false);
                            globalContext.setErrorModalProps({ errorMessage: error, errorLocation: "/Credentials/GetDenialReasonsByAuthId" });
                            globalContext.setErrorModalActive(true);
                        }
                    )
            })
        }
    }

    //AddAuthorizationComment
    function AddAuthComment() {
        if (props.AuthId && authComment !== "") {
            globalContext.GetToken().then((token: any) => {
                var headers = new Headers();
                var bearer = "Bearer " + token;
                headers.append("Authorization", bearer);
                headers.append("Content-type", "application/json;charset=UTF-8");

                var options = {
                    method: "POST",
                    headers: headers,
                };

                var url = apiConfig.apiEndpoint + "/Credentials/AddAuthorizationComment?authId=" + props.AuthId + "&text=" + authComment;
                fetch(url, options)
                    .then(res => res.json())
                    .then(
                        (result) => {
                            GetAuthorizationHistory();
                        },
                        (error) => {
                            //setLoading(false);
                            globalContext.setErrorModalProps({ errorMessage: error, errorLocation: "/Credentials/AddAuthorizationComment" });
                            globalContext.setErrorModalActive(true);
                        }
                    )
            })
        }
    }

    const HandleAddComment = () => {
        AddAuthComment();
        setAuthComment("");
    }


    function GetAuthorization() {
        if (props.AuthId) {
            setIsLoading(true);

            globalContext.GetToken().then((token: any) => {
                var headers = new Headers();
                var bearer = "Bearer " + token;
                headers.append("Authorization", bearer);
                headers.append("Content-type", "application/json;charset=UTF-8");

                var options = {
                    method: "GET",
                    headers: headers,
                };
                
                var url = apiConfig.apiEndpoint + "/Credentials/GetAuthorizationByAuthId?authId=" + props.AuthId;
                fetch(url, options)
                    .then(res => res.json())
                    .then(
                        (result) => {
                            setAuthorization(result);
                            setIsLoading(false);
                        },
                        (error) => {
                            setIsLoading(false);
                            globalContext.setErrorModalProps({ errorMessage: error, errorLocation: "/Credentials/GetAuthorizationByAuthId" });
                            globalContext.setErrorModalActive(true);
                        }
                    )
            })
        }
    }

    function GetAuthorizationDetails() {
        if (props.AuthId) {
            globalContext.GetToken().then((token: any) => {
                var headers = new Headers();
                var bearer = "Bearer " + token;
                headers.append("Authorization", bearer);
                headers.append("Content-type", "application/json;charset=UTF-8");

                var options = {
                    method: "GET",
                    headers: headers,
                };

                var url = apiConfig.apiEndpoint + "/Credentials/GetAuthorizationDetailsByAuthId?authId=" + props.AuthId;
                fetch(url, options)
                    .then(res => res.json())
                    .then(
                        (result) => {
                            setAuthorizationDetails(result);
                            setIsLoading(false);
                        },
                        (error) => {
                            //setLoading(false);
                            setIsLoading(false);
                            globalContext.setErrorModalProps({ errorMessage: error, errorLocation: "/Credentials/GetAuthorizationDetailsByAuthId" });
                            globalContext.setErrorModalActive(true);
                        }
                    )
            })
        }
    }

    function GetAuthorizationHistory() {
        globalContext.GetToken().then((token: any) => {
            var headers = new Headers();
            var bearer = "Bearer " + token;
            headers.append("Authorization", bearer);
            headers.append("Content-type", "application/json;charset=UTF-8");

            var options = {
                method: "GET",
                headers: headers,
            };

            var url = apiConfig.apiEndpoint + "/Credentials/GetAuthorizationHistory?authId=" + props.AuthId;
            fetch(url, options)
                .then(res => res.json())
                .then(
                    (result) => {
                        setAuthorizationHistory(result);
                        setIsLoading(false);
                    },
                    (error) => {
                        //setLoading(false);
                        setIsLoading(false);
                        globalContext.setErrorModalProps({ errorMessage: error, errorLocation: "/Credentials/GetAuthorizationHistory" });
                        globalContext.setErrorModalActive(true);
                    }
                )
        })
    }

    function UpdateAuthorization() {

        if (props.AuthId && saveButtonActive) {

            // get the denial reasons and store them in a list
            const denialReasons = selectedAuthDenialReason ? selectedAuthDenialReason.map(reason => reason.value) : [];
            const feedbackOptions = selectedAuthFeedbackOptions ? selectedAuthFeedbackOptions.map(option => option.value) : [];
            //send them to the backend with the other items. Other items sent in the url

            // Create the payload
            const payload = {
                denialReasons,
                feedbackOptions,
                prevAuthAction
            };

            setIsSaving(true);
            setSaveButtonActive(false);
            globalContext.GetToken().then((token: any) => {
                var headers = new Headers();
                var bearer = "Bearer " + token;
                headers.append("Authorization", bearer);
                headers.append("Content-type", "application/json;charset=UTF-8");

                var options = {
                    method: "POST",
                    headers: headers,
                    body: JSON.stringify(payload)
                };

                var url = apiConfig.apiEndpoint + "/Credentials/UpdateAuthorizations?authId=" + props.AuthId + "&reportDueDate=" + (reportDueDate ? new Date(reportDueDate).toLocaleDateString() : "") + "&reportComplete=" + (reportCompleted?.value === '1') + "&actionComplete=" + (actionComplete?.value === '1') + "&prevActionComplete=" + (prevAuthAction?.actionCompleted ?? false) + "&prevReportComplete=" + (authorization?.reportCompleted === true) + "&isBucket=" + (isBucket?.value === "1") + "&payorType=" + selectedPayorType?.value + "&authorizationStatus=" + selectedAuthStatus?.value + "&prevAuthStatus=" + prevSelectedAuthStatus?.value + "&actionId=" + (selectedAuthAction ? selectedAuthAction?.value : 0) + "&actionDueDate=" + (actionDueDate ? new Date(actionDueDate).toLocaleDateString() : "") + "&originalAuthorizationStatusId=" + authorization?.statusId + "&payorRepOnCall=" + (payorRepOnCallValue ? payorRepOnCallValue : "");
                fetch(url, options)
                    .then(res => res.json())
                    .then(
                        (result) => {
                            // update the details

                            setIsSaving(false);
                            setSaveButtonActive(true);

                            HandleCancelButtonPressed();
                        },
                        (error) => {
                            setIsSaving(false);
                            setSaveButtonActive(true);
                            globalContext.setErrorModalProps({ errorMessage: error, errorLocation: "/Credentials/UpdateAuthorizations" });
                            globalContext.setErrorModalActive(true);
                        }
                    )
            })
        }
    }

    function UpdatAuthorizationDetails() {

        if (selectedAuthStatus) {
            // Need to send the values and codes to the backend just like how I am doing it in the full denial and additional info

            var chosenImportedAuths = ezClaimAuthorizations
                .filter(auth => procedureValues.some(proc => proc.id === auth.id))
                .map(auth => ({
                    ...auth,
                    unitsRequested: procedureValues.find(proc => proc.id === auth.id)?.unitsRequested || auth.unitsRequested
                }));


            // I was told that sometimes the payor type is changed and the secondary can become the new primary, so we have to account for that.
            const allSameType = chosenImportedAuths.every(
                (auth) =>
                    auth.ezclaimAuthPayerType === chosenImportedAuths[0]?.ezclaimAuthPayerType
                    //auth.ezclaimAuthPayerType === props.AuthPayorType
            );

            if (allSameType) {
                setIsSaving(true);
                setSaveButtonActive(false);
                globalContext.GetToken().then((token: any) => {
                    var headers = new Headers();
                    var bearer = "Bearer " + token;
                    headers.append("Authorization", bearer);
                    headers.append("Content-type", "application/json;charset=UTF-8");

                    var options = {
                        method: "POST",
                        headers: headers,
                        body: JSON.stringify(chosenImportedAuths),
                    };

                    var url = apiConfig.apiEndpoint + "/Credentials/UpdateCurrentAuthsDetails?authId=" + props.AuthId;
                    fetch(url, options)
                        .then(res => res.json())
                        .then(
                            (result) => {
                                GetAuthorizationDetails();

                                setIsSaving(false);
                                setSaveButtonActive(true);

                                HandleCancelButtonPressed();
                            },
                            (error) => {
                                //setLoading(false);
                                setIsSaving(false);
                                setSaveButtonActive(true);
                                globalContext.setErrorModalProps({ errorMessage: error, errorLocation: "/Credentials/UpdateCurrentAuthsDetails" });
                                globalContext.setErrorModalActive(true);
                            }
                        )
                })
            }
            else {
                setWarningModalActive(true);
            }
            
        }
    }

    function GetAuthorizationsFromEzClaim() {
        setIsLoading(true);
        globalContext.GetToken().then((token: any) => {
            var headers = new Headers();
            var bearer = "Bearer " + token;
            headers.append("Authorization", bearer);
            headers.append("Content-type", "application/json;charset=UTF-8");


            var options = {
                method: "GET",
                headers: headers,
            };

            var url = apiConfig.apiEndpoint + "/Credentials/GetEzClaimAuthoriazationsForClient?clientId=" + props.Client?.clientId;
            fetch(url, options)
                .then(res => res.json())
                .then(
                    (result) => {
                        setEzClaimAuthorizations(result);
                        setIsLoading(false);
                    },
                    (error) => {
                        setIsLoading(false);
                        globalContext.setErrorModalProps({ errorMessage: error, errorLocation: "/Clients/GetAllClientsList" });
                        globalContext.setErrorModalActive(true);
                    }
                )
        })
    }

    function GetAuthorizationsForClient() {
        globalContext.GetToken().then((token: any) => {
            var headers = new Headers();
            var bearer = "Bearer " + token;
            headers.append("Authorization", bearer);
            headers.append("Content-type", "application/json;charset=UTF-8");


            var options = {
                method: "GET",
                headers: headers,
            };

            var url = apiConfig.apiEndpoint + "/Credentials/GetAuthorizationDetailsForClient?clientId=" + props.Client?.clientId;
            fetch(url, options)
                .then(res => res.json())
                .then(
                    (result) => {
                        setClientAuthorizations(result);
                    },
                    (error) => {
                        //setLoading(false);
                        globalContext.setErrorModalProps({ errorMessage: error, errorLocation: "/Credentials/GetAuthorizationDetailsForClient" });
                        globalContext.setErrorModalActive(true);
                    }
                )
        })
    }

    function DeleteAuthorization() {
        if (props.AuthId && props.IsInsurance) {
            globalContext.GetToken().then((token: any) => {
                var headers = new Headers();

                var bearer = "Bearer " + token;

                headers.append("Authorization", bearer);
                var options = {
                    method: "POST",
                    headers: headers
                };

                var url = "/Credentials/DeleteAuth?authId=" + props.AuthId;

                fetch(apiConfig.apiEndpoint + url, options)
                    .then(res => res.json())
                    .then(
                        (result: APIReturnObject) => {
                            if (result.status === Status.SUCCESS) {
                                setIsLoading(false);

                                globalContext.setToastMessage("Authorization Deleted.");
                                globalContext.setToastIcon("Success");
                                globalContext.setToastActive(true);

                                HandleCancelButtonPressed();
                            }
                            else if (result.status === Status.FAIL) {
                                setIsLoading(false);
                                globalContext.setNotificationModalMessage(result.message);
                                globalContext.setNotificationModalActive(true);
                            }
                            else if (result.status === Status.ERROR) {
                                setIsLoading(false);
                                globalContext.setErrorModalProps({ errorMessage: result.message, errorLocation: "/Credentials/DeleteAuth" });
                                globalContext.setErrorModalActive(true);
                            }
                        },
                        (error) => {
                            setIsLoading(false);
                            globalContext.setErrorModalProps({ errorMessage: error, errorLocation: "/Credentials/DeleteAuth" });
                            globalContext.setErrorModalActive(true);
                        }
                    )
            })
        }
    }

    function HandleSaveButtonPressed() {
        if (isUpdateView) {
            // function to update the details here
            UpdatAuthorizationDetails();

            // Go back to the regular view of the modal
            //HandleCancelButtonPressed();
        }
        else {
            UpdateAuthorization();

            // Leave the modal
            
        }

    }

    function HandleUpdateDetailsPressed() {
        setIsUpdateView(true);
        setSaveButtonActiveForUpdate(false);
    }

    const handleCheckboxChange = (id: string, isChecked: boolean) => {

        // newer logic
        setProcedureValues((prev) => {
            if (isChecked) {
                // Add if it doesn't exist already
                if (!prev.some(item => item.id === id)) {
                    return [...prev, { id, unitsRequested: "0" }]; // Default unitsRequested
                }
                return prev;
            } else {
                // Remove if it exists
                return prev.filter(item => !(item.id === id));
            }
        });
    };

    function HandleCancelButtonPressed() {
        if (isUpdateView) {
            setIsUpdateView(false);

            //clear values last so it is fast visually
            setProcedureValues([]);
            setSaveButtonActiveForUpdate(false);
        }
        else {
            CloseModal();
        }
    }

    function CloseModal() {
        props.setActive(false);
        setSaveButtonActive(false);
        setReportCompleted(null);
        setIsBucket(null);
        setSelectedPayorType(null);
        setSelectedAuthStatus(null);
        setSelectedAuthAction(null);
        setActionDueDate(null);
        setIsUpdateView(false);
        setPrevAuthAction(null);
        setPayorRepOnCallValue(null);
        setSelectedAuthDenialReason(null);
        setSelectedAuthFeedbackOptions(null);
        setActionComplete(null);

        props.OnModalClose();
    }

    function OnPayorTypeChange(input: SingleValue<ChoiceOption>) {

        if (props.PayorInfoTypes) {

            var foundPayorType = props.PayorInfoTypes?.find(z => String(z.payorInfoTypeId) === String(input?.value));
            var temp: ChoiceOption;

            if (foundPayorType) {
                temp = ({ value: String(foundPayorType.payorInfoTypeId), label: foundPayorType.payorInfoType1 })
                setSelectedPayorType(temp);
            }
            setSaveButtonActive(true);
        }

    }

    function OnAuthorizationStatusChange(input: SingleValue<ChoiceOption>) {
        if (props.AuthorizationStatuses) {
            var foundAuthorizationStatus = props.AuthorizationStatuses?.find(z => String(z.authStatusId) === String(input?.value));
            var temp: ChoiceOption;

            if (foundAuthorizationStatus) {
                temp = ({ value: String(foundAuthorizationStatus.authStatusId), label: foundAuthorizationStatus.status })
                setSelectedAuthStatus(temp);
            }
            setSaveButtonActive(true);
        }
    }

    function OnAuthActionStatusChange(input: SingleValue<ChoiceOption>) {
        if (props.AuthorizationActions) {
            var foundAuthAction = props.AuthorizationActions.find(z => String(z.authActionId) === String(input?.value));
            var temp: ChoiceOption;

            if (foundAuthAction){
                temp = ({ value: String(foundAuthAction.authActionId), label: foundAuthAction.actionText });
                setSelectedAuthAction(temp);
            }
            setSaveButtonActive(true);
        }
    }

    function OnDenialReasonChange(input: MultiValue<ChoiceOption>) {
        var finalOptions: ChoiceOption[] = [];

        if (props.AuthorizationDenialReasons) {
            input.forEach(x => {
                var CustomChoice: ChoiceOption = {
                    value: x.value,
                    label: x.label
                };

                finalOptions.push(CustomChoice);
            })
        }

        setSelectedAuthDenialReason(finalOptions);

        setSaveButtonActive(true);
    }

    function OnFeedbackOptionChange(input: MultiValue<ChoiceOption>) {
        var finalOptions: ChoiceOption[] = [];

        if (props.AuthorizationFeedbackOptions) {
            input.forEach(x => {
                var CustomChoice: ChoiceOption = {
                    value: x.value,
                    label: x.label
                };

                finalOptions.push(CustomChoice);
            })
        }

        setSelectedAuthFeedbackOptions(finalOptions);

        setSaveButtonActive(true);
    }

    const handleImportReqUnitsChange = (id: string, procedureCode: string, unitsRequested: string) => {
        setProcedureValues((prev) =>
            prev.map((item) =>
                item.id === id ? { ...item, unitsRequested } : item
            )
        );
    };

    const handleSelectAllButtonClicked = () => {
        setProcedureValues((prev) => {
            const allSelected = prev.length === ezClaimAuthorizations.length;

            if (allSelected) {
                // Deselect all
                return [];
            } else {
                // Select all
                return ezClaimAuthorizations.map(auth => ({
                    id: auth.id,
                    unitsRequested: "0"
                }));
            }
        });
    };

    const handleCopyUnitsApproved = () => {
        setProcedureValues((prev) =>
            prev.map((procedure) => {
                const match = ezClaimAuthorizations.find(
                    (auth) => auth.id === procedure.id
                );

                return match
                    ? { ...procedure, unitsRequested: match.unitsAllowed?.toString() ?? "0" }
                    : procedure;
            })
        );
    };

    function CheckIfSaveButtonActive(): boolean {
        if (!isUpdateView) {
            return saveButtonActive;
        }
        else {
            if (areAllCheckedCptCodesFilledForImport()) {
                return true;
            }
            else {
                return false;
            }
        }
    }

    const areAllCheckedCptCodesFilledForImport = () => {
        if (Object.keys(procedureValues).length > 0) {

            return true;
        }
        else {
            return false;
        }
    }

    return (
        <div className={"inset-0 z-60 relative " + (props.Active ? "normal" : "hidden")} aria-labelledby="modal-title" role="dialog" aria-modal="true">

            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

            <div className="fixed inset-0 z-60 overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all xl:m-8 w-fit h-fit">
                        <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div className="sm:flex sm:items-start">
                                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <div className="mt-4">

                                        <div className={"w-full xl:w-[60rem] h-fit m-auto mb-2 bg-gray-50 border drop-shadow rounded flex"}>

                                            <div className="flex-none p-4 h-fit m-auto hidden sm:inline">
                                                <ProfilePicture isEditable={false} Client={props.Client} setProfilePictureModalActive={null} setSelectedClientForModal={null} />
                                            </div>


                                            <div className="grid grid-cols-2 gap-x-8 h-fit text-sm">
                                                {/* First Column */}
                                                <div className="grid grid-cols-2 gap-y-2 mt-5">
                                                    <label className="font-bold text-right pr-2">Learner Name:</label>
                                                    <span className="hitespace-nowrap overflow-hidden text-ellipsis max-w-xs">{authorization ? authorization?.firstName + " " + authorization.lastName : "N/A"}</span>

                                                    <label className="font-bold text-right pr-2">Birthday:</label>
                                                    <span>{authorization ? new Date(authorization.dob).toLocaleDateString() : "N/A"}</span>

                                                    <label className="font-bold text-right pr-2">Client ID:</label>
                                                    <span className="whitespace-nowrap overflow-hidden text-ellipsis max-w-xs">{authorization?.clientId}</span>

                                                    <label className="font-bold text-right pr-2">Center:</label>
                                                    <span className="whitespace-nowrap overflow-hidden text-ellipsis max-w-xs">{authorization?.centerName}</span>

                                                    <label className="font-bold text-right pr-2">Pod Manager:</label>
                                                    <span className="whitespace-nowrap overflow-hidden text-ellipsis max-w-xs">{authorization?.podmanagerName ? authorization?.podmanagerName : "N/A" }</span>
                                                </div>

                                                {/* Second Column */}
                                                <div className="grid grid-cols-2 gap-y-2 mt-5">
                                                    <label className="font-bold text-right pr-2">Authorization ID:</label>
                                                    <span>{authorization?.authNumber}</span>

                                                    <label className="font-bold text-right pr-2">Auth Start Date:</label>
                                                    <span>{authorization?.startDate ? new Date(authorization?.startDate).toLocaleDateString() : "N/A"}</span>

                                                    <label className="font-bold text-right pr-2">Auth End Date:</label>
                                                    <span>{authorization?.endDate ? new Date(authorization?.endDate).toLocaleDateString() : "N/A"}</span>

                                                    <label className="font-bold text-right pr-2">Payor:</label>
                                                    <span>{authorization?.payorName ? authorization?.payorName : "N/A"}</span>

                                                    <label className="font-bold text-right pr-2">Authorized Provider:</label>
                                                    <span>{authorization?.providerName ? authorization?.providerName : "N/A"}</span>
                                                </div>
                                            </div>


                                        </div>

                                        <div className={(isLoading || isUpdateView ? "hidden" : "")}>
                                            <div className="ml-auto p-2 border-2 border-lacblue rounded-lg">
                                                <div className="w-full flex items-center justify-between pb-3">
                                                    <h2 className="text-2xl text-lacblue w-fit">Current Authorization</h2>

                                                    {props.IsInsurance ? <button onClick={HandleUpdateDetailsPressed} className=" justify-right bg-cyan ml-2 w-[7rem] h-[2rem] rounded font-bold text-lg text-white transition duration-250 ease-in-out hover:bg-cyan-600"><span className="drop-shadow">Update</span></button> : <></>}
                                                </div>

                                                <table className="table-auto w-full">
                                                    <thead className="w-full border border-lacblue-500 text-center">
                                                        <tr className="bg-lacblue text-white">
                                                            <th>Code</th>
                                                            <th>Mod 1</th>
                                                            <th>Mod 2</th>
                                                            <th>Units Req</th>
                                                            <th>Units Appr</th>
                                                            <th>Hours Appr</th>
                                                            <th>Hours Remaining</th>
                                                            <th>Util %</th>
                                                            <th>Payor</th>
                                                            <th>Notes</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {/* Build out dependent on the view authorizationDetails*/}
                                                        {authorizationDetailsFiltered.map(authorizationDetail =>
                                                            <tr className="border border-gray-500 text-center text-sm px-1">
                                                                <td className="border border-gray-500">{authorizationDetail.cptcode}</td>
                                                                <td className="border border-gray-500">{authorizationDetail.mod1 ? authorizationDetail.mod1 : "N/A"}</td>
                                                                <td className="border border-gray-500">{authorizationDetail.mod2 ? authorizationDetail.mod2 : "N/A"}</td>
                                                                <td className="border border-gray-500">{authorizationDetail.unitsRequested}</td>
                                                                <td className="border border-gray-500">{authorizationDetail.unitsAllowed}</td>
                                                                <td className="border border-gray-500">{authorizationDetail.hoursAllowed}</td>
                                                                <td className="border border-gray-500">{authorizationDetail.hoursRemaining ? authorizationDetail.hoursRemaining : "N/A"}</td>
                                                                <td className={"border border-gray-500 " + (authorizationDetail.proratedPercentageUtilized ? (authorizationDetail.proratedPercentageUtilized >= 90 && authorizationDetail.proratedPercentageUtilized <= 104) ? "bg-lacgreen-200" : "bg-rose-200" : "" )}>{authorizationDetail.proratedPercentageUtilized ? authorizationDetail.proratedPercentageUtilized : "N/A"}</td>
                                                                <td className="border border-gray-500">{authorizationDetail.payorName}</td>
                                                                <td className="border border-gray-500">{authorizationDetail.notes}</td>
                                                            </tr>
                                                        )}

                                                    </tbody>
                                                </table>
                                            </div>

                                            <div className="grid gap-2 grid-cols-12 xl:w-[60rem] w-full m-auto mt-2 mb-2">

                                                <div className="w-full m-auto sm:col-span-2 col-span-2">
                                                    <label className="block font-bold" htmlFor="search">
                                                        Report Due Date
                                                    </label>
                                                    <DatePicker
                                                        value={reportDueDate}
                                                        disableFuture={false}
                                                        onChange={(newValue) => {
                                                            setReportDueDate(newValue);
                                                            setSaveButtonActive(true);
                                                        }}
                                                        
                                                        renderInput={(params) => <TextField size="small" {...params} />}
                                                    />
                                                </div>
                                                <div className="w-full m-auto sm:col-span-2 col-span-2">
                                                    <label className="block font-bold" htmlFor="search">
                                                        Report Completed
                                                    </label>
                                                    <Select
                                                        components={{ ValueContainer }}
                                                        isMulti={false}
                                                        closeMenuOnSelect={true}
                                                        options={[
                                                            { value: "1", label: "Yes" },
                                                            { value: "0", label: "No" }
                                                        ]}
                                                        onChange={(newValue) => {
                                                            if (newValue) {
                                                                setReportCompleted(newValue);
                                                                setSaveButtonActive(true);
                                                            }
                                                            else {
                                                                setReportCompleted(null);
                                                                setSaveButtonActive(true);
                                                            }
                                                        }}
                                                        value={reportCompleted}
                                                    />
                                                </div>
                                                <div className="w-full m-auto sm:col-span-4 col-span-4">
                                                    <label className="block font-bold" htmlFor="search">
                                                        Bucket Information
                                                    </label>
                                                    <Select
                                                        className=""
                                                        components={{ ValueContainer }}
                                                        isMulti={false}
                                                        closeMenuOnSelect={true}
                                                        isDisabled={!props.IsInsurance}
                                                        options={[
                                                            { value: "1", label: "Bucket" },
                                                            { value: "0", label: "Non-Bucket" }
                                                        ]}
                                                        onChange={(newValue) => {
                                                            if (newValue) {
                                                                setIsBucket(newValue);
                                                                setSaveButtonActive(true);
                                                            }
                                                            else {
                                                                setIsBucket(null);
                                                                setSaveButtonActive(true);
                                                            }
                                                        }}
                                                        value={isBucket}
                                                    />
                                                </div>
                                                <div className="w-full m-auto sm:col-span-4 col-span-4">
                                                    <label className="block font-bold" htmlFor="search">
                                                        Payor Type
                                                    </label>
                                                    <Select isDisabled={!props.IsInsurance} options={props.PayorInfoTypes && props.PayorInfoTypes.length > 0 ? props.PayorInfoTypes.map((payor) => ({ value: String(payor.payorInfoTypeId), label: payor.payorInfoType1 })) : undefined} onChange={OnPayorTypeChange} value={selectedPayorType} />
                                                </div>
                                                <div className="w-full m-auto sm:col-span-4 col-span-4">
                                                    <label className="block font-bold" htmlFor="search">
                                                        Authorization Status
                                                    </label>
                                                    <Select isDisabled={!props.IsInsurance} options={props.AuthorizationStatuses && props.AuthorizationStatuses.length > 0 ? props.AuthorizationStatuses.map((authorizationStatus) => ({ value: String(authorizationStatus.authStatusId), label: authorizationStatus.status })) : undefined} onChange={OnAuthorizationStatusChange} value={selectedAuthStatus} />
                                                </div>

                                                <div className="w-full m-auto sm:col-span-3 col-span-4">
                                                    <label className="block font-bold" htmlFor="search">
                                                        Action
                                                    </label>
                                                    <Select isDisabled={!props.IsInsurance} options={filteredAuthActions && filteredAuthActions.length > 0 && selectedAuthStatus != null ? filteredAuthActions.map((authAction) => ({ value: String(authAction.authActionId), label: authAction.actionText })) : undefined} onChange={OnAuthActionStatusChange} value={selectedAuthAction} />
                                                </div>
                                                <div className="w-full m-auto sm:col-span-3 col-span-4">
                                                    <label className="block font-bold" htmlFor="search">
                                                        Action Due
                                                    </label>
                                                    <DatePicker
                                                        className="w-full"
                                                        value={actionDueDate}
                                                        disabled={!props.IsInsurance}
                                                        disableFuture={false}
                                                        onChange={(newValue) => {
                                                            setActionDueDate(newValue);
                                                            setSaveButtonActive(true);
                                                        }}
                                                        renderInput={(params) => <TextField size="small" {...params} />}
                                                    />
                                                </div>

                                                <div className="w-full m-auto sm:col-span-2 col-span-2">
                                                    <label className="block font-bold" htmlFor="search">
                                                        Action Completed
                                                    </label>
                                                    <Select
                                                        components={{ ValueContainer }}
                                                        isMulti={false}
                                                        closeMenuOnSelect={true}
                                                        options={[
                                                            { value: "1", label: "Yes" },
                                                            { value: "0", label: "No" }
                                                        ]}
                                                        onChange={(newValue) => {
                                                            if (newValue) {
                                                                setActionComplete(newValue);
                                                                setSaveButtonActive(true);
                                                            }
                                                            else {
                                                                setActionComplete(null);
                                                                setSaveButtonActive(true);
                                                            }
                                                        }}
                                                        value={actionComplete}
                                                    />
                                                </div>

                                                <div className={"w-full m-auto sm:col-span-12 col-span-12 " + (showDenialReasonsDropdown ? "" : "hidden" )}>
                                                    <label className="block font-bold" htmlFor="search">
                                                        Denial Reasons
                                                    </label>
                                                    <Select isDisabled={!props.IsInsurance} isMulti={true} closeMenuOnSelect={false} options={props.AuthorizationDenialReasons && props.AuthorizationDenialReasons.length > 0 ? props.AuthorizationDenialReasons.map((reason) => ({ value: String(reason.authDenialReasonId), label: reason.reasonText })) : undefined} onChange={(selectedAuthDenialReason: OnChangeValue<ChoiceOption, true>) => OnDenialReasonChange(selectedAuthDenialReason)} value={selectedAuthDenialReason} />
                                                </div>

                                                {/*P2P inputs here*/}

                                                <div className={"w-full m-auto sm:col-span-12 col-span-12 " + (showP2PInputs ? "" : "hidden")}>
                                                    <label className="block font-bold" htmlFor="search">
                                                        Specific Feedback
                                                    </label>
                                                    <Select isDisabled={props.IsInsurance} isMulti={true} closeMenuOnSelect={false} options={props.AuthorizationFeedbackOptions && props.AuthorizationFeedbackOptions.length > 0 ? props.AuthorizationFeedbackOptions.map((reason) => ({ value: String(reason.authFeedbackLookupId), label: reason.feedbackText })) : undefined} onChange={(selectedAuthFeedbackOptions: OnChangeValue<ChoiceOption, true>) => OnFeedbackOptionChange(selectedAuthFeedbackOptions)} value={selectedAuthFeedbackOptions} />
                                                </div>

                                                <div className={"w-full m-auto sm:col-span-4 col-span-4 " + (showP2PInputs ? "" : "hidden")}>
                                                    <label className="block font-bold" htmlFor="search">
                                                        Payor Rep On Call (Full Name and Prefix)
                                                    </label>
                                                    <input
                                                        maxLength={250}
                                                        autoComplete="off"
                                                        type="text"
                                                        disabled={props.IsInsurance}
                                                        value={payorRepOnCallValue ? payorRepOnCallValue : ""}
                                                        onChange={e => setPayorRepOnCallValue(e.target.value) }
                                                        className="outline-none h-[38px] border focus:border-2 border-gray-300 text-gray-900 rounded focus:ring-lacblue focus:border-lacblue w-full p-2.5"
                                                    />
                                                </div>

                                            </div>

                                            <div className="">
                                                <label className="block font-bold mt-2">
                                                    Add Comment:
                                                </label>
                                                <div className="flex items-center">
                                                    <input
                                                        maxLength={500}
                                                        autoComplete="off"
                                                        type="text"
                                                        value={authComment}
                                                        onChange={(event) => setAuthComment(event.target.value)}
                                                        className="outline-none h-[38px] border focus:border-2 border-gray-300 text-gray-900 rounded focus:ring-lacblue focus:border-lacblue w-full p-2.5"
                                                    />
                                                    <button onClick={() => HandleAddComment()} className="ml-2 bg-lacyellow w-[10rem] h-[2.5rem] rounded font-bold text-lg text-white transition duration-250 ease-in-out hover:bg-lacyellow-600">
                                                        <span className="drop-shadow">Add Comment</span>
                                                    </button>
                                                </div>
                                            </div>

                                            <span className="font-bold">Comments:</span>
                                            <div className="w-full border-2">
                                                <div className="max-h-[120px] overflow-y-auto">
                                                    <table className="w-full">
                                                        <tbody>
                                                            {authorizationHistory && authorizationHistory.length > 0 ? (
                                                                authorizationHistory.map(comment => (
                                                                    <tr key={comment.authHistoryId}>
                                                                        <td><span className="font-bold">[{comment.updatedOn ? format(new Date(comment.updatedOn), 'M/d/yy') : ""}] {comment.updatedBy}:</span> {comment.text}</td>
                                                                    </tr>
                                                                ))
                                                            ) : (
                                                                <tr><td className="text-rose">No Comments.</td></tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Update View here */}
                                        <div className={"ml-auto p-2 border-2 border-lacblue rounded-lg " + (isUpdateView && !isLoading ? '' : 'hidden')}>
                                            <div className="w-full flex my-1 items-center justify-start">

                                                <h2 className="text-2xl text-lacblue w-fit">Update EZ Claim Authorizations</h2>

                                                {/* The container for the buttons with ml-auto to push it to the right */}
                                                <div className="ml-auto flex items-center text-right pr-2">
                                                    <button onClick={() => { handleSelectAllButtonClicked() }} className="bg-lacgreen mx-2 my-auto w-fit h-[2.5rem] px-2 rounded font-bold text-lg text-white transition duration-250 ease-in-out hover:bg-lacgreen-600">
                                                        <span className="drop-shadow">Select All</span>
                                                    </button>

                                                    <button onClick={() => { handleCopyUnitsApproved() }} className="bg-purple mx-2 my-auto w-fit h-[2.5rem] px-2 rounded font-bold text-lg text-white transition duration-250 ease-in-out hover:bg-purple-600">
                                                        <span className="drop-shadow">Copy Units Apv.</span>
                                                    </button>

                                                </div>

                                            </div>

                                            <table className="table-auto w-full">
                                                <thead className="w-full border border-lacblue-500 text-center">
                                                    <tr className="bg-lacblue text-white">
                                                        <th>Select</th>
                                                        <th>Auth #</th>
                                                        <th>Code</th>
                                                        <th>Units Req.</th>
                                                        <th>Units Apv.</th>
                                                        <th>Mod 1</th>
                                                        <th>Mod 2</th>
                                                        <th>Authorized BCBA</th>
                                                        <th>Payor</th>
                                                        <th>Payor Type</th>
                                                        <th>Start Date</th>
                                                        <th>End Date</th>
                                                        <th>Notes</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {/* Build out dependent on the view */}

                                                    {ezClaimAuthorizations && ezClaimAuthorizations.length > 0 ? ezClaimAuthorizations.map(authorization =>
                                                        <tr className="border border-gray-500 text-center">
                                                            <td className="border border-gray-500">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={procedureValues.some(item => item.id === authorization.id)}
                                                                    onChange={(e) => handleCheckboxChange(authorization.id, e.target.checked)}
                                                                />
                                                            </td>
                                                            <td className="border border-gray-500"><span>{authorization.authNumber}</span></td>
                                                            <td className="border border-gray-500"><span>{authorization.procedureCode}</span></td>
                                                            <td className="border border-gray-500"><input className="w-[4.5rem] px-2 py-1 border border-gray-300 rounded" type="number" value={procedureValues.find(x => x.id === authorization.id)?.unitsRequested || ""} onChange={(e) => handleImportReqUnitsChange(authorization.id, authorization.procedureCode, e.target.value)}></input></td>
                                                            <td className="border border-gray-500"><span>{authorization.unitsAllowed}</span></td>
                                                            <td className="border border-gray-500"><span>{authorization.mod1}</span></td>
                                                            <td className="border border-gray-500"><span>{authorization.mod2}</span></td>
                                                            <td className="border border-gray-500"><span>{authorization.phyName}</span></td>
                                                            <td className="border border-gray-500"><span>{authorization.payerName}</span></td>
                                                            <td className="border border-gray-500"><span>{authorization.ezclaimAuthPayerType}</span></td>
                                                            <td className="border border-gray-500"><span>{authorization.startDate ? new Date(authorization.startDate).toLocaleDateString() : "N/A"}</span></td>
                                                            <td className="border border-gray-500"><span>{authorization.endDate ? new Date(authorization.endDate).toLocaleDateString() : "N/A"}</span></td>
                                                            <td className="border border-gray-500"><span>{authorization.notes}</span></td>
                                                        </tr>
                                                    ) : <tr><td colSpan={8} className="text-center text-rose">No Authorizations.</td></tr>}
                                                </tbody>
                                            </table>

                                        </div>


                                        {/* loading here */}
                                        <div className={"w-full h-auto flex " + (isLoading ? "" : "hidden")}>
                                            <FontAwesomeIcon className="fill-current text-lacblue w-24 h-24 animate-spin m-auto" icon={solid('spinner')} />
                                        </div>
                                    </div>

                                    
                                </div>
                            </div>
                        </div>
                        <div className="bg-gray-50 px-4 py-2 flex items-center justify-center relative sm:px-6">
                            {/* Trash can button on far left using absolute positioning */}
                            <div className={"absolute left-4 " + (props.IsInsurance ? "" : "hidden")}>
                                <button
                                    onClick={() => setDeleteAuthorizationModal(true)}
                                >
                                    <FontAwesomeIcon
                                        className="text-lacblue w-5 h-5 px-1 hover:cursor-pointer"
                                        icon={solid('trash-can')}
                                    />
                                </button>
                            </div>

                            {/* Centered Save and Cancel buttons */}

                            <div className="flex space-x-2">
                                <button
                                    onClick={HandleSaveButtonPressed}
                                    className={
                                        "w-[7rem] h-[2rem] rounded font-bold text-lg text-white transition duration-250 ease-in-out bg-lacgreen " +
                                        (CheckIfSaveButtonActive()
                                            ? 'hover:bg-lacgreen-600'
                                            : 'opacity-50 cursor-not-allowed')
                                    }
                                    disabled={!CheckIfSaveButtonActive()}
                                >
                                    {isSaving ? (
                                        <FontAwesomeIcon
                                            className="fill-current text-white w-5 h-5 animate-spin m-auto"
                                            icon={solid('spinner')}
                                        />
                                    ) : (
                                        <span className="drop-shadow">Save</span>
                                    )}
                                </button>
                                <button
                                    onClick={HandleCancelButtonPressed}
                                    className="bg-rose w-[7rem] h-[2rem] rounded font-bold text-lg text-white transition duration-250 ease-in-out hover:bg-rose-600"
                                >
                                    <span className="drop-shadow">Cancel</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ModalNotification Title={"Warning!"} Body={"You've selected items with different payor types. Please choose items that all share the same payor type to proceed."} Active={warningModalActive} Icon={"Warning"} setActive={setWarningModalActive} OnModalClose={function noop() { }} />
            <ModalConfirm Message={"Are you sure you want to delete this Authorization for this learner?"} Active={deleteAuthorizationModal} setActive={setDeleteAuthorizationModal} OnConfirm={() => DeleteAuthorization()} />
        </div>
    )

}